// Applicant-facing view of an application's progress (My Applications and the
// applicant detail page). The office workflow (task T1..T10) is folded into
// four steps an applicant cares about.
import { statusOf as deskStatusOf } from "@/components/Common/deskConfig";

// Licence issued and still alive: an application stays "Licence issued" while
// the desks finish their work, after a refund of the security deposit, and
// while a cancellation request is waiting for the division office. Once the
// cancellation is accepted it is Cancelled and moves to the cancelled list.
export function isLicensed(app) {
  return !!app && !!app.license && (app.status === "Active" || app.status === "Cancelrequested");
}

// Status pill as the applicant reads it.
export function statusOf(app) {
  const status = deskStatusOf(app);
  if (!app) return status;
  if (app.status === "Active" && app.task === "T0") return { label: "Returned to you", tone: status.tone };
  if (isLicensed(app)) return { label: "Licence issued", tone: "success" };
  return status;
}

// How a rejected or licensed application ended: when, and what the applicant
// is told. null for the others (they show who holds them instead). A rejection
// is the last thing logged on an application, so its last update stands in
// when the exact time isn't known.
export function outcomeOf(app) {
  if (!app) return null;
  if (app.status === "Rejected") {
    const reason = app.rejectReasondfo;
    return {
      kind: "rejected",
      icon: "mdi-close-octagon-outline",
      title: "Rejected",
      when: app.rejectedAt || app.updated_at,
      note: reason && reason !== "Nill" ? reason : "",
      flag: "",
    };
  }
  if (isLicensed(app)) {
    return {
      kind: "licence",
      icon: "mdi-file-certificate-outline",
      title: "Licence issued",
      when: app.licenseDate,
      note: app.licenseNo ? "Licence No. " + app.licenseNo : "",
      flag: app.status === "Cancelrequested" || app.cancelRequest === "Yes" ? "Cancellation requested" : "",
    };
  }
  return null;
}

// Desk that holds an application at each step of the office workflow, with the
// stage it belongs to (the same T1..T10 the offices work on).
const DESK_OF_TASK = {
  T1: ["Section Clerk", "Application review"],
  T2: ["Superintendent", "Application review"],
  T3: ["Divisional Forest Officer", "Application review"],
  T4: ["Section Clerk", "After fee payment"],
  T5: ["Superintendent", "After fee payment"],
  T6: ["Divisional Forest Officer", "After fee payment"],
  T8: ["Section Clerk", "After site inspection"],
  T9: ["Superintendent", "After site inspection"],
  T10: ["Divisional Forest Officer", "After site inspection"],
};

// Where an application is right now, for the applicant: who has it and what
// they are doing with it. Never names an officer. `tone`: you | office | range | closed.
export function whereIs(app) {
  if (!app) return { who: "—", detail: "", tone: "closed", icon: "mdi-help-circle-outline" };
  if (app.status === "Rejected") return { who: "Closed", detail: "Rejected by the division office", tone: "closed", icon: "mdi-close-octagon-outline" };
  if (app.status === "Cancelled") return { who: "Closed", detail: "Cancelled", tone: "closed", icon: "mdi-cancel" };
  if (app.status === "Cancelrequested" || app.cancelRequest === "Yes") {
    return { who: "Division office", detail: "Reviewing your cancellation request", tone: "office", icon: "mdi-office-building-outline" };
  }
  if (app.status !== "Active") return { who: "Closed", detail: app.status || "", tone: "closed", icon: "mdi-lock-outline" };
  if (app.task === "T0") return { who: "With you", detail: "Returned for changes — edit it and submit again", tone: "you", icon: "mdi-account-arrow-left-outline" };
  if (app.paymentInitiation === "YES" && app.paymentStatus !== "Success") {
    return { who: "With you", detail: "Approved — pay the fees to continue", tone: "you", icon: "mdi-cash-clock" };
  }
  if (app.task === "T7") return { who: "Range office", detail: "Site inspection of the shooting location", tone: "range", icon: "mdi-pine-tree" };
  const desk = DESK_OF_TASK[app.task];
  if (desk) return { who: desk[0], detail: desk[1], tone: "office", icon: "mdi-office-building-outline" };
  if (app.isforwardtoRange === "yes") return { who: "Range office", detail: "Site inspection of the shooting location", tone: "range", icon: "mdi-pine-tree" };
  return { who: "Division office", detail: "Under review", tone: "office", icon: "mdi-office-building-outline" };
}

export const APPLICANT_STEPS = [
  { key: "submitted", label: "Submitted", icon: "mdi-send-check-outline" },
  { key: "review", label: "Division review", icon: "mdi-account-search-outline" },
  { key: "payment", label: "Fee payment", icon: "mdi-cash-check" },
  { key: "licence", label: "Licence issued", icon: "mdi-file-certificate-outline" },
];

// Number of completed steps (1..4).
export function stepsDone(app) {
  if (!app) return 0;
  if (app.license) return 4;
  if (app.paymentStatus === "Success") return 3;
  if (app.paymentInitiation === "YES") return 2;
  return 1;
}

// One line telling the applicant what is happening now.
export function nextStepText(app) {
  if (!app) return "";
  if (app.status === "Rejected") return "This application was rejected by the division office.";
  if (app.status === "Cancelled") return "This application was cancelled.";
  if (app.status === "Cancelrequested" || app.cancelRequest === "Yes") return "Your cancellation request is with the division office.";
  if (app.task === "T0") return "Returned to you — please review the remarks, edit and resubmit.";
  if (app.paymentInitiation === "YES" && app.paymentStatus !== "Success") return "Approved — pay the fees to continue.";
  if (app.license) return "Licence issued. Carry it during the shoot.";
  if (app.paymentStatus === "Success") return "Payment received — the licence is being prepared.";
  return "Under review at the division office.";
}

// Applicant can still pay the fees.
export function canPay(app) {
  return !!app && app.status === "Active" && app.paymentInitiation === "YES" && app.paymentStatus === "Pending";
}

// Applicant can ask to extend after the range officers have reported.
export function canExtend(app) {
  return !!app && app.isExtended === false && !!(app.rangeMahassars && app.rangeMahassars.length) && !!(app.rangeReports && app.rangeReports.length);
}

// Opens a PDF generated by the backend (Bearer auth) in a new tab. The tab is
// opened first so pop-up blockers allow it.
export function openPdf(axios, url, appid) {
  const tab = window.open("", "_blank");
  return axios({
    method: "GET",
    url,
    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
    params: { appid },
    responseType: "blob",
  })
    .then((response) => {
      const blobUrl = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      if (tab) tab.location.href = blobUrl;
      else window.open(blobUrl, "_blank");
    })
    .catch((err) => {
      if (tab) tab.close();
      throw err;
    });
}
